import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Car } from "../../types/car.types";
import {
  addCar,
  getCars,
  removeCar,
  updateCar,
} from "../../services/carService";
import toast from "react-hot-toast";

interface CarState {
  cars: Car[];
}

const initialState: CarState = { cars: [] };

export const addMyCar = createAsyncThunk(
  "post/cars",
  async (
    {
      number,
      brand,
      model,
      carType,
      kms,
      insuranceExpiry,
      year,
      fuelType,
      lastServiceKms,
      lastServiceDate,
      driveFrequency,
    }: Car,
    { rejectWithValue }
  ) => {
    const { data, error } = await addCar(
      number,
      brand,
      model,
      carType,
      kms,
      insuranceExpiry,
      year,
      fuelType,
      lastServiceKms,
      lastServiceDate,
      driveFrequency
    );

    if (error) return rejectWithValue(error);

    return {
      _id: data?._id,
      message: data?.message,
      number,
      brand,
      model,
      kms,
      insuranceExpiry,
      year,
      fuelType,
      lastServiceKms,
      lastServiceDate,
      driveFrequency,
    };
  }
);

export const getMyCars = createAsyncThunk(
  "get/cars",
  async (ids: string[], { rejectWithValue }) => {
    const { data, error } = (await getCars(ids)) as {
      data: { cars: Car[] };
      error: any;
    };

    if (error) return rejectWithValue(error);

    return data;
  }
);

export const updateMyCar = createAsyncThunk(
  "patch/car",
  async (
    { updateDetails, carId }: { updateDetails: Partial<Car>; carId: string },
    { rejectWithValue }
  ) => {
    const { data, error } = (await updateCar(updateDetails, carId)) as {
      data: { car: Partial<Car>; message: string };
      error: any;
    };
    if (error) rejectWithValue(error);

    return data;
  }
);

export const removeMyCar = createAsyncThunk(
  "delete/car",
  async ({ carId }: { carId: string }, { rejectWithValue }) => {
    const { data, error } = (await removeCar(carId)) as {
      data: { message: string; _id?: string };
      error: any;
    };
    if (error) rejectWithValue(error);
    return data;
  }
);

export const carSlice = createSlice({
  name: "myCars",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(addMyCar.fulfilled, (state, action) => {
      state.cars.push(action?.payload as any);
      toast.success(action?.payload?.message as string);
    });
    builder.addCase(getMyCars.fulfilled, (state, action) => {
      state.cars = action?.payload.cars;
    });
    builder.addCase(updateMyCar.fulfilled, (state, action) => {
      const updatedCar = action.payload?.car;
      const index = state.cars.findIndex((c) => c._id === updatedCar._id);

      if (index !== -1) {
        state.cars[index] = {
          ...state.cars[index],
          ...updatedCar,
        };
      }
      toast.success(action.payload.message);
    });
    builder.addCase(removeMyCar.fulfilled, (state, action) => {
      const removedCarId = action?.payload._id;
      if (removedCarId) {
        state.cars = state.cars.filter((car) => car?._id !== removedCarId);
      }
      toast.success(action.payload.message);
    });
  },
});

export default carSlice.reducer;
